import { Button, Checkbox, FormControlLabel, Grid, Paper, Typography } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import TextFieldWithDirtyValidation from './form/TextFieldWithDirtyValidation';
import { Link } from 'react-router';
import { AccountType } from './constants/securspace-constants';
import classNames from 'classnames';
import request from '../util/SuperagentUtils';
import { getErrorMessage } from '../util/NetworkErrorUtil';
import Busy from './Busy';
import { validateEmail, validatePassword, validatePhoneNumber } from '../util/ValidationUtils';
import SimpleRecaptcha from './form/SimpleRecaptcha';
import { Alert } from '@mui/material';
import PhoneInput from "react-phone-number-input/input";
import {PhoneNumberTextFieldWithDirtyValidation} from "./form/PhoneNumberTextField";
import type {Theme} from "@mui/material";
import {withSnackbar, WithSnackbarProps} from "./hocs/withSnackbar";
import TermsAndPolicyCheckbox from "./TermsAndPolicyCheckbox";
import { isBuyerAccountType } from "../util/AccountUtils";
import { useFeatureFlags } from "../hooks/routing/useFeatureFlags";
import type {Account} from "../types/Account";

interface SocialLoginUser {
  user?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

interface SignUpFormProps extends WithSnackbarProps {
  type?: string;
  handleAccountChange: (account: Account) => void;
  socialLoginTemporaryUser?: SocialLoginUser;
  externalForm?: boolean;
  externalSubmitForm?: () => void;
  utmParams?: Record<string, string>;
}

const signUpContainerSx = {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '0.57rem',
    overflow: 'hidden',
    width: '100%',
} as const;

const signUpFormContainerSx = (theme: Theme) => ({
    padding: '2.5em',
    [theme.breakpoints.up('md')]: {
        padding: '3rem 4.6rem 0 4.6rem',
    },
});


const submitButtonSx = {
    marginTop: '1.71em',
    marginBottom: "1em",
} as const;

const partnerSignUpFormTitleSx = {
    fontWeight: 700,
} as const;

const loginLinkContainerSx = {
    marginTop: '1.71rem',
    height: '4.29rem',
    backgroundColor: '#FAFAFA',
    borderRadius: '0 0 0.57rem 0.57rem',
} as const;


const appInputSx = {
    marginBottom: '0.6rem',
    '& label': {
         fontSize: '1.14em',
         fontWeight: 400,
         fontFamily: 'Inter',
         lineHeight: '0.71em',
         letterSpacing: '.05em',
         color: 'rgba(0, 0, 0, 0.38)',
    },
} as const;

const SignUpForm = (props: SignUpFormProps) => {
    const type = props.type || AccountType.BUYER;
    const snackbarShowMessage = props.snackbarShowMessage;
    const {externalForm = false, externalSubmitForm, utmParams = {}} = props;
    const { buyerTextOptIn } = useFeatureFlags();

    const [companyName, setCompanyName] = useState('');
    const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState('Please enter your company name.');
    const [firstName, setFirstName] = useState(props.socialLoginTemporaryUser?.user ? props.socialLoginTemporaryUser.user.first_name ?? '' : '');
    const [firstNameErrorMsg, setFirstNameErrorMsg] = useState('Please enter your first name.');
    const [lastName, setLastName] = useState(props.socialLoginTemporaryUser?.user ? props.socialLoginTemporaryUser.user.last_name ?? '' : '');
    const [lastNameErrorMsg, setLastNameErrorMsg] = useState('Please enter your last name.');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState('Please enter a phone number.');
    const [email, setEmail] = useState(props.socialLoginTemporaryUser?.user ? props.socialLoginTemporaryUser.user.email ?? '' : '');
    const [emailErrorMsg, setEmailErrorMsg] = useState('Please enter an email address.');
    const [password, setPassword] = useState('');
    const [passwordErrorMsg, setPasswordErrorMessage] = useState('Please enter a password.');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordErrorMsg, setConfirmPasswordErrorMsg] = useState('Passwords do not match.');
    const [isOptedIntoText, setIsOptedIntoText] = useState(true);
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [agreementAcceptedErrorMsg, setAgreementAcceptedErrorMsg] = useState('Please accept the agreements.');
    // A ref, not state: the widget instance is only read from submit handlers, and
    // storing it in state turns every ref attach/detach into a render.
    const recaptchaInstance = useRef<{ reset: () => void } | null>(null);
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const [recaptchaErrorMsg, setRecaptchaErrorMsg] = useState('');
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const [formErrorMsg, setFormErrorMsg] = useState('');

    const disableCaptcha = import.meta.env.VITE_DISABLE_RECAPTCHA === 'true';

    const simpleTextValidation = (value: string, label: string): string => {
        if (!value || !value.trim()) {
            return `Please enter your ${label}.`;
        }
        return '';
    };

    const confirmPasswordValidation = (value: string): string => {
        if (!value || !value.trim()) {
            return "Please enter a password.";
        }
        return value === password ? '' : 'Passwords do not match.';
    };

    const phoneNumberValidation = (value: string): string => {
        if (!value || !value.trim()) return 'Please enter a phone number.';
        return validatePhoneNumber(value, true) ? '' : 'Phone number must be 10 digits.';
    };

    const emailValidation = (value: string): string => {
        if (!value || !value.trim()) return 'Please enter an email address.';
        return validateEmail(value) ? '' : 'Email address is invalid.';
    };

    const handleSimpleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = event.target.name;
        const fieldValue = event.target.value;
        switch (fieldName) {
            case 'companyName':
                setCompanyName(fieldValue);
                setCompanyNameErrorMsg(simpleTextValidation(fieldValue, 'company name'));
                break;
            case 'firstName':
                setFirstName(fieldValue);
                setFirstNameErrorMsg(simpleTextValidation(fieldValue, 'first name'));
                break;
            case 'lastName':
                setLastName(fieldValue);
                setLastNameErrorMsg(simpleTextValidation(fieldValue, 'last name'));
                break;
            case 'email':
                setEmail(fieldValue);
                setEmailErrorMsg(emailValidation(fieldValue));
                break;
            case 'confirmPassword':
                setConfirmPassword(fieldValue);
                setConfirmPasswordErrorMsg(confirmPasswordValidation(fieldValue));
                break;
            default:
                break;
        }
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setAgreementAccepted(checked);
        setAgreementAcceptedErrorMsg(checked ? '' : 'Please accept the agreement.');
    };

    const handlePhoneNumberChange = (value?: string) => {
        setPhoneNumber(value || '');
        setPhoneNumberErrorMsg(phoneNumberValidation(value || ''));
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setPassword(value);
        setPasswordErrorMessage(validatePassword(value));
        if (value !== confirmPassword) {
            setConfirmPasswordErrorMsg('Passwords do not match.');
        }
    };

    const verifyRecaptchaCallback = (response: string) => {
        setRecaptchaToken(response);
    };

    const handleExpiredRecaptcha = () => {
        setRecaptchaToken('');
    };

    const handleRecaptchaLoaded = () => {
        setRecaptchaLoaded(true);
    };

    const handleRecaptchaRef = useCallback((ref: { reset: () => void } | null) => {
        recaptchaInstance.current = ref;
    }, []);


    const signup = async (body: Record<string, string | boolean>) => {
        return request.post('/api/signup').send(body);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!disableCaptcha) {
            if (!recaptchaLoaded) {
                setRecaptchaErrorMsg('Unable to verify recaptcha, please try again or refresh the page.');
                return;
            } else if (!recaptchaToken) {
                setRecaptchaErrorMsg('Please verify that you are a human.');
                return;
            } else {
                setRecaptchaErrorMsg('');
            }
        }

        if (companyNameErrorMsg ||
            firstNameErrorMsg ||
            lastNameErrorMsg ||
            phoneNumberErrorMsg ||
            emailErrorMsg ||
            passwordErrorMsg ||
            confirmPasswordErrorMsg ||
            agreementAcceptedErrorMsg
        ) {
            setFormErrorMsg('Please complete the form to proceed.');
            return;
        } else {
            setFormErrorMsg('');
        }
        const body = {
            type: type,
            email: email,
            firstName: firstName,
            lastName: lastName,
            phoneNumber: phoneNumber,
            username: email,
            password: password,
            companyName: companyName,
            ...(buyerTextOptIn && isBuyerAccountType(type) ? { isOptedIntoText } : {}),
            ...utmParams,
            recaptchaToken
        };
        Busy.set(true);
        signup(body).then(response => {
            Busy.set(false);
            if (!disableCaptcha) {
                recaptchaInstance.current?.reset();
                setRecaptchaToken('');
            }
            if (response.body && response.body.id) {
                props.handleAccountChange(response.body);
                if (externalForm) {
                    externalSubmitForm?.();
                }
            } else {
                setFormErrorMsg("Unable to create account. Please try again.");
            }
        }).catch(error => {
            Busy.set(false);
            if (!disableCaptcha) {
                recaptchaInstance.current?.reset();
                setRecaptchaToken('');
            }
            const errorMessage = getErrorMessage(error);
            setFormErrorMsg(errorMessage);

            if(!externalForm) {
                snackbarShowMessage(errorMessage, 'error', 15000);
            }
        });
    };

    return (
        <Paper sx={signUpContainerSx} elevation={2} >
            <Grid container sx={signUpFormContainerSx} >
                {isBuyerAccountType(type) ?
                    <Typography variant='h5' component='h1' sx={partnerSignUpFormTitleSx}>Sign Up as a Buyer</Typography>
                    : <Typography variant='h5' component='h1' sx={partnerSignUpFormTitleSx}>Sign Up as a Supplier</Typography>
                }
                <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', rowGap: '0.71rem', marginTop: '1.43rem', width: '100%'}} noValidate>
                    <TextFieldWithDirtyValidation
                        value={companyName}
                        onChange={handleSimpleTextChange}
                        name='companyName'
                        label='Company Name'
                        placeholder='Please enter your company name'
                        error={!!companyNameErrorMsg}
                        helperText={companyNameErrorMsg}
                        fullWidth
                        required
                    />
                    <TextFieldWithDirtyValidation
                        value={firstName}
                        onChange={handleSimpleTextChange}
                        name='firstName'
                        label='First Name'
                        placeholder='Enter your first name'
                        error={!!firstNameErrorMsg}
                        helperText={firstNameErrorMsg}
                        fullWidth
                        required
                    />
                    <TextFieldWithDirtyValidation
                        value={lastName}
                        onChange={handleSimpleTextChange}
                        name='lastName'
                        label='Last Name'
                        placeholder='Enter your last name'
                        error={!!lastNameErrorMsg}
                        helperText={lastNameErrorMsg}
                        fullWidth
                        required
                    />
                    <PhoneInput
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      inputComponent={PhoneNumberTextFieldWithDirtyValidation}
                      defaultCountry={"US"}
                      error={!!phoneNumberErrorMsg}
                      helperText={phoneNumberErrorMsg}
                      fullWidth
                      required
                      className={undefined}
                      style={appInputSx as React.CSSProperties}
                    />
                    {buyerTextOptIn && isBuyerAccountType(type) && (
                    <FormControlLabel
                        sx={{ alignItems: 'flex-start' }}
                        control={
                            <Checkbox
                                checked={isOptedIntoText}
                                onChange={(e) => setIsOptedIntoText(e.target.checked)}
                                name="isOptedIntoText"
                                sx={{ paddingTop: 0 }}
                            />
                        }
                        label={
                            <Typography variant="body2" component="span">
                                <strong>Send me text message notifications about my bookings, payments, and account updates.</strong>
                                {' '}Msg &amp; data rates may apply. Msg frequency varies. You will receive a text message if it is a valid mobile number.
                            </Typography>
                        }
                    />
                    )}
                    <TextFieldWithDirtyValidation
                        value={email}
                        onChange={handleSimpleTextChange}
                        name='email'
                        label='Email'
                        placeholder='Enter your email'
                        type='email'
                        error={!!emailErrorMsg}
                        helperText={emailErrorMsg}
                        fullWidth
                        required
                    />
                    <TextFieldWithDirtyValidation
                        value={password}
                        onChange={handlePasswordChange}
                        name='password'
                        label='Password'
                        placeholder='Create a password with at least 8 characters'
                        type='password'
                        error={!!passwordErrorMsg}
                        helperText={passwordErrorMsg}
                        fullWidth
                        required
                    />
                    <TextFieldWithDirtyValidation
                        value={confirmPassword}
                        onChange={handleSimpleTextChange}
                        name='confirmPassword'
                        label='Confirm Password'
                        placeholder='Repeat your password'
                        type='password'
                        error={!!confirmPasswordErrorMsg}
                        helperText={confirmPasswordErrorMsg}
                        fullWidth
                        required
                    />
                    <TermsAndPolicyCheckbox checked={agreementAccepted} onChange={handleCheckboxChange} accountType={type} name={'agreementAccepted'} externalForm={externalForm} />
                    {
                        !disableCaptcha &&
                            <SimpleRecaptcha
                            setRef={handleRecaptchaRef}
                            verifyCallback={verifyRecaptchaCallback}
                            expiredCallback={handleExpiredRecaptcha}
                            onloadCallback={handleRecaptchaLoaded}
                            />
                    }

                    {
                        recaptchaErrorMsg && <Alert severity='error'>{recaptchaErrorMsg}</Alert>
                    }
                    {
                        formErrorMsg && <Alert severity='error'>{formErrorMsg}</Alert>
                    }
                    <Button type='submit' variant='contained' color='secondary' fullWidth sx={submitButtonSx}>Agree &amp; Sign Up</Button>
                </form>
            </Grid>
            {
                !externalForm ?
                    <Grid container className={classNames('w-100')} sx={loginLinkContainerSx} alignContent='center'>
                        <Typography variant="subtitle2" align='center' color="textPrimary" className='w-100'>
                            Already have an account? <Link to={{ pathname: "/login" }} style={{color: 'inherit'}}>Log In</Link>
                        </Typography>
                    </Grid>
                : ""
            }
        </Paper>
    );
};

export default withSnackbar(SignUpForm);
